/*  CreatorShell — layout + auth guard for the Certification Author portal.
    ────────────────────────────────────────────────────────────────────────
    Mounted at /creator/*. This portal is a **content-authoring workbench**
    (long-form course descriptions, rich-text lessons, video previews,
    quiz + final-exam authoring), so it deliberately keeps a full-height
    left sidebar + a full-width main content area — no top header, no
    max-width cap. The tradeoff vs the CoE portal is intentional:
    Planners work in cards; Authors work in canvases.

    What we *do* carry over from CoE for consistency:
      • ProfileCard at the top of the sidebar (photo / initials + name + role)
      • Role label follows the `CoE-<Role>` convention
      • Same nav-item pill styling (primary-fill when active)
      • ErrorBoundary around <Outlet />

    Access matrix:
      • role='staff' + staff_type='course_creator'  → allowed
      • role='admin' | 'super_admin'                → allowed (impersonation-friendly)
      • anyone else                                 → bounced to their own portal
*/
import React, { useEffect, useState } from "react";
import { Outlet, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    Books, SignOut, GraduationCap, User as UserIcon, Article,
    SidebarSimple, Broadcast, ShieldCheck,
} from "@phosphor-icons/react";
import InvisiqueLogo from "@/components/site/Logo";
import ErrorBoundary from "@/components/site/ErrorBoundary";
import { useNotificationToasts } from "@/lib/useNotificationToasts";


// Certification Author portal is a tight, focused workbench —
// "Dashboard" was a duplicate of "My Courses", and the "New Course" CTA
// already lives inside the page header where the courses live.
// iter100.154 — Blog authoring added alongside Courses; same approval
// pipeline (submit → admin review → publish/reject).
const NAV = [
    { to: "/creator/courses",         icon: Books,       label: "My Courses" },
    { to: "/creator/blog",            icon: Article,     label: "My Blog Posts" },
    { to: "/creator/webinars",        icon: Broadcast,   label: "Live Sessions" },
    // iter100.171 — Certification Authors now share the editorial
    // review pool for doctor-authored content (articles, case studies,
    // testimonials). Same review UI as the admin portal — RBAC is
    // enforced server-side via require_content_reviewer.
    { to: "/creator/content-review",  icon: ShieldCheck, label: "Content Review" },
    { to: "/creator/profile",         icon: UserIcon,    label: "My Profile" },
];

// iter100.156c — On laptop viewports (14"), the workbench felt cramped:
// left rail (256) + modules (320) + lessons (240) = 816 px of chrome,
// leaving only ~500 px for the RichTextEditor. Collapsing this rail to
// a 64 px icon-only strip claws back 192 px. Users can expand it back
// at any time.
// iter100.198a — Persistence switched from `localStorage` →
// `sessionStorage` per user direction: every fresh login starts from
// the viewport default (collapsed on ≤1440 px laptops, expanded on
// larger screens). Any in-session collapse toggle is remembered only
// for THIS session. Mirrors the doctor + admin portals.
const COLLAPSED_KEY = "invq_creator_shell_collapsed";


export default function CreatorShell() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // Default collapsed on laptop viewports (≤1440 px) so authors have
    // the widest possible canvas the moment they land.
    const [collapsed, setCollapsed] = useState(() => {
        // iter100.267 — Universal "default expanded" per user direction.
        // Drop any legacy localStorage value so returning users get the
        // fresh behaviour immediately.
        try { localStorage.removeItem(COLLAPSED_KEY); } catch { /* noop */ }
        const stored = sessionStorage.getItem(COLLAPSED_KEY);
        if (stored === "1") return true;
        if (stored === "0") return false;
        return false;
    });
    useEffect(() => {
        sessionStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    }, [collapsed]);
    // iter100.155 — Fire any pending in-app toasts (blog approvals /
    // rejections). Guarded on `user` so it only runs after auth resolves.
    useNotificationToasts({ enabled: Boolean(user) });

    if (loading) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
    if (!user) return <Navigate to="/coe-login" replace state={{ from: location }} />;

    const allowed =
        (user.role === "staff" && user.staff_type === "course_creator") ||
        user.role === "admin" ||
        user.role === "super_admin";
    if (!allowed) return <Navigate to="/" replace />;

    // Match the CoE-<Role> convention (see TYPE_LABELS in CoeLayout.jsx).
    const typeLabel = "Certification Author";

    return (
        <div className="min-h-screen bg-secondary/30 flex" data-testid="creator-shell">
            {/* Sidebar — full height so the workbench has an anchor and the
                content area on the right has the whole viewport width.
                Collapsible on laptop viewports (see COLLAPSED_KEY). */}
            <aside
                className={`shrink-0 bg-card border-r border-border sticky top-0 h-screen flex flex-col transition-[width] duration-200 ${collapsed ? "w-16" : "w-64"}`}
                data-testid="creator-shell-sidebar"
                data-collapsed={collapsed ? "true" : "false"}
            >
                <div className={`border-b border-border flex items-center gap-2 ${collapsed ? "px-2 py-3 justify-center" : "px-5 py-4"}`}>
                    {collapsed ? (
                        <NavLink to="/creator/courses" className="w-9 h-9 rounded-xl bg-primary grid place-items-center shrink-0 relative overflow-hidden" title="Invisique">
                            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
                            <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 relative" aria-hidden="true">
                                <path d="M12 2 L13.6 8.5 L20 10 L13.6 11.5 L12 18 L10.4 11.5 L4 10 L10.4 8.5 Z" fill="white" />
                            </svg>
                        </NavLink>
                    ) : (
                        <InvisiqueLogo size="sm" />
                    )}
                </div>

                {/* Profile card — compact when collapsed */}
                <div className={collapsed ? "px-2 pt-3" : "px-3 pt-3"}>
                    <ProfileCard user={user} typeLabel={typeLabel} collapsed={collapsed} />
                </div>

                {/* Nav */}
                <nav className={`mt-3 flex-1 ${collapsed ? "px-2" : "px-2"}`}>
                    {NAV.map((item) => <SidebarItem key={item.to} item={item} collapsed={collapsed} />)}
                </nav>

                {/* Bottom: expand/collapse toggle + logout.
                    iter100.267 — Universal teal circular icon toggle
                    (matches PortalLayout + AdminLayout). */}
                <div className={`border-t border-border ${collapsed ? "p-2 space-y-1.5" : "p-3 space-y-2"} flex flex-col items-center`}>
                    <button
                        type="button"
                        onClick={() => setCollapsed((v) => !v)}
                        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                        aria-pressed={collapsed}
                        className={`flex items-center justify-center w-8 h-8 rounded-full border border-primary/25 text-primary bg-primary/5 hover:bg-primary/15 hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors ${collapsed ? "rotate-180" : ""}`}
                        data-testid="creator-shell-collapse-toggle"
                    >
                        <SidebarSimple size={16} weight="duotone" />
                    </button>
                    <Button
                        variant="outline"
                        size="sm"
                        className={`w-full gap-1.5 ${collapsed ? "px-0" : ""}`}
                        onClick={() => { logout(); navigate("/coe-login"); }}
                        title="Log out"
                        data-testid="creator-logout-btn"
                    >
                        <SignOut size={13} />
                        {!collapsed && <span>Log out</span>}
                    </Button>
                </div>
            </aside>

            {/* Main — full remaining width, no max cap, so content-authoring
                surfaces (rich text, video previews, split-view builders)
                have room to breathe. */}
            <main className="flex-1 min-w-0">
                <ErrorBoundary routeLabel="this page">
                    <Outlet />
                </ErrorBoundary>
            </main>
        </div>
    );
}


/* Compact identity card mirroring CoeLayout's ProfileCard. */
function ProfileCard({ user, typeLabel, collapsed }) {
    const name = user?.name || "Author";
    const photo = user?.image || "";
    const initials = (name || "?")
        .split(/\s+/).filter(Boolean).slice(0, 2)
        .map((w) => w[0]?.toUpperCase()).join("") || "?";
    return (
        <NavLink
            to="/creator/profile"
            className={`block bg-secondary/40 border border-border rounded-2xl hover:border-primary/40 transition-colors group no-underline ${collapsed ? "p-1.5" : "p-3"}`}
            data-testid="creator-sidebar-profile-card"
            title={collapsed ? `${name} · ${typeLabel}` : undefined}
        >
            <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
                <div
                    className={`rounded-full overflow-hidden grid place-items-center shrink-0 ring-2 ring-primary/10 group-hover:ring-primary/30 transition ${collapsed ? "w-9 h-9" : "w-10 h-10"}`}
                    style={photo ? {} : { background: "linear-gradient(135deg, hsl(280 60% 55%), hsl(200 70% 50%))" }}
                    data-testid="creator-sidebar-profile-avatar"
                >
                    {photo ? (
                        <img src={photo} alt={name} className="w-full h-full object-cover" />
                    ) : (
                        <span className="text-white text-sm font-display font-bold">{initials}</span>
                    )}
                </div>
                {!collapsed && (
                    <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate" data-testid="creator-sidebar-profile-name">{name}</div>
                        <div className="text-[9.5px] tracking-[0.16em] uppercase text-muted-foreground truncate flex items-center gap-1" data-testid="creator-sidebar-profile-role">
                            <GraduationCap size={9} weight="fill" /> {typeLabel}
                        </div>
                    </div>
                )}
            </div>
        </NavLink>
    );
}


function SidebarItem({ item, collapsed }) {
    return (
        <NavLink
            to={item.to}
            data-testid={`creator-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
                `flex items-center rounded-lg text-sm font-medium transition-colors no-underline mb-0.5 ${
                    collapsed ? "justify-center h-10 w-full" : "gap-2.5 px-3 py-2"
                } ${
                    isActive
                        ? "bg-primary text-white"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
            }
        >
            <item.icon size={16} weight="duotone" />
            {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
        </NavLink>
    );
}
